import { Select, TextField } from '@/components/ds2'
import { CHARGE_TYPE_OPTIONS, chargeAmountOf, type ChargeDraft } from './reviewModel'

interface ChargesEditorProps {
    charges: ChargeDraft[]
    onChange: (charges: ChargeDraft[]) => void
}

const emptyCharge = (): ChargeDraft => ({ id: 'charge', type: '', amount: '', description: '' })

/** The one charge the drawer edits, and how a change to it is handed back. */
const useCharge = ({ charges, onChange }: ChargesEditorProps) => {
    const charge = charges[0] ?? emptyCharge()
    return { charge, update: (patch: Partial<ChargeDraft>) => onChange([{ ...charge, ...patch }]) }
}

/**
 * The approved additional charges of one invoice (v17): the amount, with the approved charge type beside it, as one cell of the invoice
 * details' field grid.
 */
export const ChargeAmountField = (props: ChargesEditorProps) => {
    const { charge, update } = useCharge(props)
    return (
        <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
                <TextField
                    label="Approved additional charges ($)"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={charge.amount}
                    onChange={(event) => update({ amount: event.target.value })}
                />
            </div>
            <div className="w-32 shrink-0">
                <Select aria-label="Charge type" placeholder="Type" options={CHARGE_TYPE_OPTIONS} value={charge.type} onChange={(type) => update({ type })} />
            </div>
        </div>
    )
}

/** The charge's description and supporting reference: shown only once the amount is above zero; the drawer checks it before it creates the draft. */
export const ChargeDescriptionField = (props: ChargesEditorProps) => {
    const { charge, update } = useCharge(props)
    if (chargeAmountOf(charge.amount) <= 0) return null
    return (
        <TextField
            label="Charge description / supporting reference"
            placeholder="Required for additional charges"
            value={charge.description}
            onChange={(event) => update({ description: event.target.value })}
        />
    )
}
